import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { tokens } from "../theme";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

export default function ConfirmationDialog({
  title,
  description,
  open,
  onClose,
  onConfirm,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('common');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[500],
          borderRadius: 4,
          overflow: "hidden",
          border: `1px solid ${colors.primary[200]}30`,
          boxShadow: `0 18px 40px ${colors.primary[900]}60`,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[700]} 90%)`,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            backgroundColor: `${colors.primary[500]}aa`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 10px 22px ${colors.primary[900]}45`,
          }}
        >
          <WarningAmberOutlinedIcon
            sx={{ fontSize: 28, color: colors.redAccent[200] }}
          />
        </Box>
        <Typography
          variant="h5"
          sx={{ color: colors.grey[100], fontWeight: 700, letterSpacing: 0.4 }}
        >
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3.5,
          py: 3,
          backgroundColor: colors.primary[500],
        }}
      >
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Typography
            variant="body1"
            sx={{
              mt: 1.5,
              color: colors.grey[100],
              fontWeight: 600,
              letterSpacing: 0.2,
            }}
          >
            {t('areYouSure')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.grey[300],
              opacity: 0.8,
              lineHeight: 1.6,
            }}
          >
            {description}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: `${colors.primary[600]}55`,
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            flex: 1,
            borderRadius: 3,
            borderColor: colors.blueAccent[400],
            color: colors.blueAccent[200],
            fontWeight: 700,
            textTransform: "none",
            letterSpacing: 0.3,
            "&:hover": {
              borderColor: colors.blueAccent[300],
              backgroundColor: `${colors.blueAccent[700]}35`,
            },
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          sx={{
            flex: 1,
            borderRadius: 3,
            textTransform: "none",
            letterSpacing: 0.3,
            fontWeight: 700,
            color: colors.grey[100],
            background: `linear-gradient(135deg, ${colors.redAccent[500]} 0%, ${colors.redAccent[600]} 100%)`,
            boxShadow: `0 12px 28px ${colors.redAccent[900]}45`,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 16px 32px ${colors.redAccent[900]}60`,
            },
          }}
        >
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}